import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { deliveryState, removeControlDeliveryByIdAction, } from "../../app/reducers/delivery-slice";
import theme from "../../style/theme";
import { ControlDelivery } from "../../types/delivery/control-delivery";
import { DeliveryStatus } from "../../types/delivery/delivery-status";
import { DeliveryGroupType } from "../../types/schedule/delivery-group-type";
import { timeFormat } from "../../utils/date/date-format";
import BlueCheckFillIcon from "../icons/BlueCheckFillIcon/BlueCheckFillIcon";
import YellowCheckFillIcon from "../icons/YellowCheckFillIcon";
import { SmallBadgeCard } from "./SmallBadgeCard";

interface Props {
    controlDelivery: ControlDelivery;
}

export const ControlDeliveryCard: React.FC<Props> = (
    {
        controlDelivery
    }
) => {
    const dispatch = useAppDispatch();
    const { controlDeliveries } = useAppSelector(deliveryState);
    const isControlDeliveryPickUp =
        controlDelivery.type === DeliveryGroupType.PickUp;
    const isControlDeliveryDropOff =
        controlDelivery.type === DeliveryGroupType.DropOff;

    const convertSelectedDisplayDeliveryItemTypeToText = (
        controlDelivery: ControlDelivery
    ) => {
        switch (controlDelivery.type) {
            case DeliveryGroupType.PickUp:
                return "상차";
            default:
                return "전달";
        }
    };

    const getSelectedDisplayDeliveryItemAddress = (
        controlDelivery: ControlDelivery
    ) => {
        if (isControlDeliveryPickUp) {
            const { alias, detail } = controlDelivery.delivery.pickUpWorkPlace;
            return `${alias} ${detail}`;
        }
        const { alias, detail } = controlDelivery.delivery.dropOffWorkPlace;
        return `${alias} ${detail}`;
    };

    const isIncludeDeliveryInControlDeliveries = (deliveryId: string) => {
        return controlDeliveries
            .map((controlDelivery) => controlDelivery.delivery.id)
            .includes(deliveryId);
    };

    const loadLabel =
        (controlDelivery.delivery.load.name === "null" ||
            controlDelivery.delivery.load.name === ""
            ? "물품 현장 확인"
            : controlDelivery.delivery.load.name) +
        (controlDelivery.delivery.load.quantity ? ` ${controlDelivery.delivery.load.quantity}` : '');

    const isMatched = controlDelivery.delivery.status !== DeliveryStatus.Wait;
    const disabled = isControlDeliveryPickUp
        ? controlDelivery.delivery.pickUpDisabled
        : controlDelivery.delivery.dropOffDisabled;

    return (
        <ContentItemContainer
            isInclude={isIncludeDeliveryInControlDeliveries(
                controlDelivery.delivery.id
            )}
            isPickUp={isControlDeliveryPickUp}
            disabled={disabled}
        >
            <TopFlexContainer>
                <IconContainer
                    disabled={controlDelivery.delivery.pickUpDisabled}
                    onClick={() =>
                        dispatch(
                            removeControlDeliveryByIdAction({
                                deliveryId: controlDelivery.delivery.id,
                            })
                        )
                    }
                >
                    {isControlDeliveryPickUp && <BlueCheckFillIcon />}
                    {isControlDeliveryDropOff && <YellowCheckFillIcon />}
                </IconContainer>
                <ContentItemLabelContainer>
                    <ContentItemLabel>
                        {convertSelectedDisplayDeliveryItemTypeToText(controlDelivery)}
                    </ContentItemLabel>
                </ContentItemLabelContainer>
                <ContentItemDeliveryAddressLabel
                    title={getSelectedDisplayDeliveryItemAddress(controlDelivery)}
                >
                    {getSelectedDisplayDeliveryItemAddress(controlDelivery)}
                </ContentItemDeliveryAddressLabel>
            </TopFlexContainer>
            <BottomFlexContainer>
                <ContentTimeLabel>
                    {isControlDeliveryPickUp &&
                        timeFormat(controlDelivery.delivery.pickUpAction.startTimeLimited)}
                    {isControlDeliveryDropOff &&
                        timeFormat(controlDelivery.delivery.dropOffAction.startTimeLimited)}
                </ContentTimeLabel>
                <LoadLabelContainer title={loadLabel}>{loadLabel}</LoadLabelContainer>
                {isMatched && <SmallBadgeCard label="배정완료" color="red" />}
            </BottomFlexContainer>
        </ContentItemContainer>
    );
};

interface ContentItemContainerProps {
    isInclude: boolean;
    isPickUp: boolean;
    disabled: boolean;
}

const ContentItemContainer = styled.div<ContentItemContainerProps>`
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  background: ${(props) => {
        if (props.disabled) {
            return theme.palette.primitives.gray50;
        }
        if (props.isInclude) {
            if (props.isPickUp) {
                return theme.palette.primitives.blue50;
            }
            return theme.palette.primitives.yellow50;
        }
    }};
  border-bottom: 1px solid ${theme.palette.primitives.gray100};
`;

const TopFlexContainer = styled.div`
  display: flex;
`;

const BottomFlexContainer = styled.div`
  display: flex;
  padding-left: 29px;
  align-items: center;
`;

const ContentItemLabelContainer = styled.div`
  padding: 0px 23px 0px 12px;
`;

const ContentItemLabel = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray600};
`;

const ContentTimeLabel = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray600};
  width: 37px;
`;

const ContentItemDeliveryAddressLabel = styled.div`
  max-width: 280px;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray900};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LoadLabelContainer = styled.div`
  max-width: 280px;
  margin: 0 10px;

  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray600};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface IconContainerProps {
    disabled: boolean;
}

const IconContainer = styled.div<IconContainerProps>`
  cursor: pointer;
  visibility: ${(props) => (props.disabled ? "hidden" : "visible")};
`;
